/**
 * WebSocket-First Bill Processing Agent
 *
 * Dedicated component for WebSocket-based bill processing.
 * Uses WSBillService with prepare → connect → start flow.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import WSBillService from '../../services/WSBillService';

const WSBillAgent = () => {
  const navigate = useNavigate();

  // Service instance
  const serviceRef = useRef(null);

  // State
  const [file, setFile] = useState(null);
  const [runId, setRunId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, preparing, connecting, running, completed, error
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Initialize service
  useEffect(() => {
    serviceRef.current = new WSBillService();

    const service = serviceRef.current;

    // Set up event listeners
    service.on('connected', (data) => {
      console.log('✅ WSBillAgent: WebSocket connected', data);
      setWsConnected(true);
      setStatus('connected');
    });

    service.on('disconnected', (data) => {
      console.log('🔌 WSBillAgent: WebSocket disconnected', data);
      setWsConnected(false);
    });

    service.on('error', (data) => {
      console.error('❌ WSBillAgent: Error', data);
      setError(data.error?.message || 'WebSocket error');
    });

    service.on('workflow_started', (data) => {
      console.log('🚀 WSBillAgent: Workflow started', data);
      setStatus('running');
      addMessage({
        type: 'system',
        content: 'Workflow started',
        timestamp: data.timestamp
      });
    });

    service.on('step_started', (data) => {
      console.log('📍 WSBillAgent: Step started', data);
      setCurrentStep(data.step_name);
      addMessage({
        type: 'step',
        content: `Starting: ${data.step_name}`,
        step: data.step_name,
        timestamp: data.timestamp
      });
    });

    service.on('step_completed', (data) => {
      console.log('✅ WSBillAgent: Step completed', data);
      addMessage({
        type: 'step',
        content: `Completed: ${data.step_name}`,
        step: data.step_name,
        status: data.status,
        timestamp: data.timestamp
      });
    });

    service.on('chat_message', (data) => {
      console.log('💬 WSBillAgent: Chat message', data);
      addMessage({
        type: 'chat',
        content: data.content,
        role: data.role,
        step: data.step_name,
        timestamp: data.timestamp
      });
    });

    service.on('workflow_completed', (data) => {
      console.log('🏁 WSBillAgent: Workflow completed', data);
      setStatus('completed');
      setCurrentStep(null);
      addMessage({
        type: 'system',
        content: `Workflow completed: ${data.status}`,
        timestamp: data.timestamp
      });
    });

    // Generic message handler for any other messages
    service.on('message', (data) => {
      console.log('📨 WSBillAgent: Message', data.type, data);

      // Handle AI response messages
      if (data.type === 'ai_response' || data.type === 'assistant') {
        addMessage({
          type: 'chat',
          content: data.content || data.message,
          role: 'assistant',
          step: data.step_name,
          timestamp: data.timestamp
        });
      }

      // Handle tool messages
      if (data.type === 'tool_call' || data.type === 'tool_result') {
        addMessage({
          type: 'tool',
          content: data.content || JSON.stringify(data.result || data.tool_name),
          tool: data.tool_name,
          step: data.step_name,
          timestamp: data.timestamp
        });
      }
    });

    // Cleanup on unmount
    return () => {
      if (serviceRef.current) {
        serviceRef.current.disconnect();
      }
    };
  }, []);

  // Add message helper
  const addMessage = useCallback((msg) => {
    setMessages(prev => [...prev, {
      ...msg,
      id: Date.now() + Math.random(),
      timestamp: msg.timestamp || new Date().toISOString()
    }]);
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setMessages([]);
      setStatus('idle');
    }
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
      setMessages([]);
      setStatus('idle');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Start processing - WebSocket-first flow
  const handleStartProcessing = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    const service = serviceRef.current;
    setError(null);
    setMessages([]);

    try {
      // Step 1: Prepare (upload file, get run_id)
      setStatus('preparing');
      addMessage({ type: 'system', content: 'Uploading file...' });

      const prepareResult = await service.prepare(file);

      if (!prepareResult.success) {
        throw new Error(prepareResult.error || 'Failed to prepare workflow');
      }

      setRunId(prepareResult.run_id);
      addMessage({ type: 'system', content: `File uploaded. Run ID: ${prepareResult.run_id}` });

      // Navigate to the dedicated chat page for this workflow
      navigate(`/bill-ws-chat/${prepareResult.run_id}`);
      return; // The chat page will handle WebSocket connection and workflow execution

      // Step 2: Connect WebSocket
      setStatus('connecting');
      addMessage({ type: 'system', content: 'Connecting WebSocket...' });

      const connected = service.connect(prepareResult.run_id);

      if (!connected) {
        throw new Error('Failed to connect WebSocket');
      }

      // Wait for WebSocket to actually connect
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        const unsubscribe = service.on('connected', () => {
          clearTimeout(timeout);
          unsubscribe();
          resolve();
        });

        const errorUnsub = service.on('error', (err) => {
          clearTimeout(timeout);
          errorUnsub();
          reject(new Error(err.error?.message || 'WebSocket connection failed'));
        });
      });

      addMessage({ type: 'system', content: 'WebSocket connected!' });

      // Step 3: Start processing
      setStatus('starting');
      addMessage({ type: 'system', content: 'Starting workflow processing...' });

      const startResult = await service.start(prepareResult.run_id);

      if (!startResult.success) {
        throw new Error(startResult.message || startResult.error || 'Failed to start workflow');
      }

      setStatus('running');
      // workflow_started event will be received via WebSocket

    } catch (err) {
      console.error('❌ WSBillAgent: Processing error', err);
      setError(err.message);
      setStatus('error');
      addMessage({ type: 'error', content: err.message });
    }
  };

  // Reset / New Upload
  const handleReset = () => {
    if (serviceRef.current) {
      serviceRef.current.disconnect();
    }
    setFile(null);
    setRunId(null);
    setStatus('idle');
    setMessages([]);
    setCurrentStep(null);
    setError(null);
    setWsConnected(false);
  };

  // Get message style based on type
  const getMessageStyle = (type) => {
    const styles = {
      system: 'bg-blue-50 border-l-4 border-blue-500',
      chat: 'bg-gray-50 border-l-4 border-purple-500',
      step: 'bg-green-50 border-l-4 border-green-500',
      tool: 'bg-orange-50 border-l-4 border-orange-500',
      error: 'bg-red-50 border-l-4 border-red-500'
    };
    return styles[type] || 'bg-gray-50 border-l-4 border-gray-400';
  };

  const getTypeBadgeStyle = (type) => {
    const styles = {
      system: 'bg-blue-500 text-white',
      chat: 'bg-purple-500 text-white',
      step: 'bg-green-500 text-white',
      tool: 'bg-orange-500 text-white',
      error: 'bg-red-500 text-white'
    };
    return styles[type] || 'bg-gray-500 text-white';
  };

  const getStatusColor = (s) => {
    const colors = {
      idle: 'text-gray-500',
      preparing: 'text-orange-500',
      connecting: 'text-blue-500',
      connected: 'text-green-500',
      running: 'text-blue-500',
      completed: 'text-green-500',
      error: 'text-red-500'
    };
    return colors[s] || 'text-gray-500';
  };

  // Render message based on type
  const renderMessage = (msg) => {
    return (
      <div key={msg.id} className={`mb-3 p-3 rounded-lg text-sm ${getMessageStyle(msg.type)}`}>
        <div className="flex flex-wrap gap-2 items-center mb-1">
          <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded ${getTypeBadgeStyle(msg.type)}`}>
            {msg.type}
          </span>
          {msg.step && (
            <span className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded">
              {msg.step}
            </span>
          )}
          {msg.tool && (
            <span className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded">
              {msg.tool}
            </span>
          )}
          <span className="text-xs text-gray-400 ml-auto">
            {new Date(msg.timestamp).toLocaleTimeString()}
          </span>
        </div>
        <div className="leading-relaxed whitespace-pre-wrap break-words">
          {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}
        </div>
      </div>
    );
  };

  return (
    <div className="p-5 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">WebSocket Bill Processing</h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className={`w-2.5 h-2.5 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
          <span>{wsConnected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-gray-50 rounded-lg p-5 mb-5">
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-50 bg-white relative"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl">📄</span>
              <span className="font-medium text-gray-800">{file.name}</span>
              <span className="text-gray-500 text-sm">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
          ) : (
            <div className="text-gray-500">
              <span>Drop PDF here or click to select</span>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-4 justify-center">
          <button
            className="px-6 py-2.5 bg-blue-500 text-white rounded-md font-medium text-sm transition-all hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleStartProcessing}
            disabled={!file || status === 'running' || status === 'preparing' || status === 'connecting'}
          >
            {status === 'preparing' ? 'Uploading...' :
             status === 'connecting' ? 'Connecting...' :
             status === 'running' ? 'Processing...' :
             'Start Processing'}
          </button>

          <button
            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-md font-medium text-sm transition-all hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleReset}
            disabled={status === 'preparing' || status === 'connecting'}
          >
            Reset
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 flex items-center gap-2">
            <span>⚠️</span>
            {error}
          </div>
        )}
      </div>

      {/* Status Bar */}
      {runId && (
        <div className="flex flex-wrap gap-5 p-4 bg-blue-50 rounded-lg mb-5">
          <div className="flex gap-2">
            <span className="text-gray-500 text-sm">Run ID:</span>
            <span className="font-medium text-gray-800 text-sm">{runId}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 text-sm">Status:</span>
            <span className={`font-medium text-sm ${getStatusColor(status)}`}>{status}</span>
          </div>
          {currentStep && (
            <div className="flex gap-2">
              <span className="text-gray-500 text-sm">Current Step:</span>
              <span className="font-medium text-gray-800 text-sm">{currentStep}</span>
            </div>
          )}
        </div>
      )}

      {/* Messages Section */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <h3 className="m-0 p-4 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-800">
          Messages ({messages.length})
        </h3>
        <div className="max-h-[500px] overflow-y-auto p-4">
          {messages.length === 0 ? (
            <div className="text-center text-gray-400 py-10">
              No messages yet. Upload a file and start processing.
            </div>
          ) : (
            messages.map(renderMessage)
          )}
        </div>
      </div>
    </div>
  );
};

export default WSBillAgent;
